import React, { useRef, useState, useCallback } from 'react';
import './DragDropFileUpload.css';

const DragDropFileUpload = ({
  onFileSelected,
  onFileRemoved,
  selectedFile = null,
  accept = "*/*",
  maxFileSize = 10 * 1024 * 1024, // 10MB default
  supportedTypes = [],
  placeholder = "Click to browse or drag & drop here",
  description = "Supports: PDF, DOCX, TXT, PPT, PPTX",
  className = "",
  disabled = false,
  multiple = false,
  showFileInfo = true
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = (file) => {
    // Check if file exists and is a proper File object
    if (!file || !(file instanceof File)) {
      alert('Invalid file selected.');
      return false;
    }

    // Check file size
    if (file.size > maxFileSize) {
      const limitLabel =
        maxFileSize >= 1024 * 1024 * 1024
          ? `${(maxFileSize / (1024 * 1024 * 1024)).toFixed(0)}GB`
          : `${Math.round(maxFileSize / (1024 * 1024))}MB`;
      const sizeLabel = formatFileSize(file.size);
      alert(
        `File too large (${sizeLabel}). Please upload files smaller than ${limitLabel}.`
      );
      return false;
    }

    // Check file type if supportedTypes are specified
    if (supportedTypes.length > 0) {
      const fileName = file.name || '';
      const fileExtension = '.' + fileName.split('.').pop().toLowerCase();
      if (!supportedTypes.includes(fileExtension)) {
        alert(`Unsupported file type. Please upload ${supportedTypes.join(', ')} files.`);
        return false;
      }
    }

    return true;
  };

  const handleFileSelect = useCallback((files) => {
    if (!files) return;

    let fileArray;
    
    // Handle FileList, single file, or file array
    if (files instanceof FileList) {
      // Convert FileList to array
      fileArray = Array.from(files);
    } else if (Array.isArray(files)) {
      fileArray = files;
    } else {
      // Single file
      fileArray = [files];
    }
    
    if (fileArray.length === 0) return;

    // Validate all files
    const validFiles = fileArray.filter(file => validateFile(file));
    
    if (validFiles.length > 0) {
      // Always pass an array to onFileSelected, let the parent decide how to handle it
      onFileSelected(validFiles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- validateFile and filter deps intentionally minimal
  }, [maxFileSize, supportedTypes, onFileSelected, multiple, validateFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    if (disabled) return;

    const files = e.dataTransfer.files;
    handleFileSelect(files);
  }, [disabled, handleFileSelect]);

  const handleClick = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  const handleFileInputChange = useCallback((e) => {
    const files = e.target.files;
    handleFileSelect(files);
  }, [handleFileSelect]);

  const handleFileRemove = useCallback((e) => {
    e.stopPropagation();
    if (onFileRemoved) {
      onFileRemoved();
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onFileRemoved]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  return (
    <div className={`drag-drop-file-upload ${className}`}>
      <div 
        className={`curriculum-file-drop-zone ${selectedFile && (Array.isArray(selectedFile) ? selectedFile.length > 0 : true) ? 'has-file' : ''} ${isDragOver ? 'drag-over' : ''} ${disabled ? 'disabled' : ''}`}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={selectedFile && (Array.isArray(selectedFile) ? selectedFile.length > 0 : true) ? 
          (Array.isArray(selectedFile) ? 
            `Selected ${selectedFile.length} file${selectedFile.length > 1 ? 's' : ''}. Click to change or drag and drop new files.` : 
            `Selected file: ${selectedFile.name}. Click to change or drag and drop a new file.`) : 
          "Click to browse files or drag and drop files here"}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          disabled={disabled}
        />
        
        {selectedFile && (Array.isArray(selectedFile) ? selectedFile.length > 0 : true) ? (
          <div>
            <i className="fas fa-file-check" style={{ fontSize: '2rem', color: '#28a745', marginBottom: '10px' }}></i>
            <div style={{ fontWeight: 'bold', color: '#28a745' }}>
              {Array.isArray(selectedFile) ? `${selectedFile.length} File${selectedFile.length > 1 ? 's' : ''} Selected!` : 'File Selected!'}
            </div>
            {Array.isArray(selectedFile) ? (
              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '5px' }}>
                {selectedFile.map((file, index) => (
                  <div key={index} style={{ marginBottom: '2px' }}>
                    {file.name} {showFileInfo && `(${formatFileSize(file.size)})`}
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '5px' }}>{selectedFile.name}</div>
                {showFileInfo && (
                  <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '5px' }}>
                    {formatFileSize(selectedFile.size)}
                  </div>
                )}
              </>
            )}
            <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '5px' }}>
              Click to change file{Array.isArray(selectedFile) && selectedFile.length > 1 ? 's' : ''} or drag & drop a new one
            </div>
            <button
              type="button"
              onClick={handleFileRemove}
              style={{
                background: 'none',
                border: 'none',
                color: '#dc3545',
                fontSize: '0.8rem',
                marginTop: '8px',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
              title="Remove file"
            >
              Remove file{Array.isArray(selectedFile) && selectedFile.length > 1 ? 's' : ''}
            </button>
          </div>
        ) : (
          <div>
            <i className="fas fa-cloud-upload-alt" style={{ fontSize: '2rem', color: '#6c757d', marginBottom: '10px' }}></i>
            <div style={{ fontWeight: 'bold', color: '#6c757d', marginBottom: '5px' }}>
              {placeholder}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#999' }}>
              {description}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DragDropFileUpload;
